import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Input, Spin, Switch } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import useAxiosClient from "../../../axios-client.js";

/**
 * A role's permission set.
 *
 * Three things carry the redesign, and all three are consequences of one
 * decision — toggles change local state, not the server:
 *
 *   - Search and grouping make one permission findable among 33.
 *   - The group header is a tri-state Checkbox, so a whole group is one click.
 *   - A save bar docks at the foot of the pane and commits the diff in a single
 *     PUT, replacing ~30 sequential requests.
 *
 * It also removes the old screen's failure modes: there is no per-row loading
 * state left to disable every other switch with, and no `message` shadowing in
 * an error path that only ran when something had already gone wrong.
 */

const GROUP_FALLBACK = 'Ungrouped';

/** Which rows a search term keeps, and why — the "why" is shown on the row. */
const matchOf = (row, term) => {
  if (!term) return { hit: true, on: null };
  const t = term.toLowerCase();
  if (row.permission.toLowerCase().includes(t)) return { hit: true, on: 'name' };
  if ((row.description || '').toLowerCase().includes(t)) return { hit: true, on: 'description' };
  if ((row.group || '').toLowerCase().includes(t)) return { hit: true, on: 'group' };
  return { hit: false, on: null };
};

const Chip = ({ kind, children }) => (
  <span className={`et-access__chip et-access__chip--${kind}`}>{children}</span>
);

function RolePermissions({ role, onDirtyChange, onSaved, readOnly = false }) {
  const axiosClient = useAxiosClient();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // The edit buffer: permission name -> boolean. Baseline stays untouched so
  // the diff is always "what would this save do", not "what have I clicked".
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const searchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const response = await axiosClient.get(`/roles/getRolePermissions?role=${encodeURIComponent(role)}`);
        if (cancelled) return;
        setRows(response.data);
        setDraft(Object.fromEntries(response.data.map((r) => [r.permission, r.value])));
        setCollapsed({});
        setSearch('');
      } catch (error) {
        if (cancelled) return;
        const message = (error.response && error.response.data && error.response.data.message)
          || error.message || error.toString();
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [role, axiosClient]);

  /* ---- Diff ----------------------------------------------------------- */
  const { added, removed } = useMemo(() => {
    const a = [];
    const r = [];
    rows.forEach((row) => {
      const next = !!draft[row.permission];
      if (next && !row.value) a.push(row.permission);
      if (!next && row.value) r.push(row.permission);
    });
    return { added: a, removed: r };
  }, [rows, draft]);

  const dirty = added.length > 0 || removed.length > 0;
  const pendingTotal = useMemo(
    () => Object.values(draft).filter(Boolean).length,
    [draft],
  );

  useEffect(() => {
    onDirtyChange?.({ dirty, added: added.length, removed: removed.length, pendingTotal });
  }, [dirty, added.length, removed.length, pendingTotal, onDirtyChange]);

  /* ---- Grouping + filtering -------------------------------------------- */
  const groups = useMemo(() => {
    const term = search.trim();
    const byGroup = new Map();

    rows.forEach((row) => {
      const key = row.group || GROUP_FALLBACK;
      if (!byGroup.has(key)) byGroup.set(key, { name: key, rows: [], matched: [] });
      const bucket = byGroup.get(key);
      const match = matchOf(row, term);
      bucket.rows.push(row);
      if (match.hit) bucket.matched.push({ ...row, matchedOn: match.on });
    });

    return [...byGroup.values()]
      // A term drops groups with nothing in them rather than showing an empty header.
      .filter((g) => (term ? g.matched.length > 0 : true))
      .map((g) => {
        const visible = term ? g.matched : g.rows;
        const on = g.rows.filter((r) => draft[r.permission]).length;
        const changed = g.rows.filter(
          (r) => !!draft[r.permission] !== r.value,
        ).length;
        // The toggle acts on what is visible; when filtered that is the matches,
        // which is why its label says so.
        const togglable = visible.filter((r) => !r.locked && !r.blocked);
        const allOn = togglable.length > 0 && togglable.every((r) => draft[r.permission]);
        const someOn = togglable.some((r) => draft[r.permission]);
        return {
          ...g,
          visible,
          on,
          changed,
          togglable,
          allOn,
          indeterminate: someOn && !allOn,
        };
      });
  }, [rows, search, draft]);

  const hiddenCount = useMemo(() => {
    if (!search.trim()) return 0;
    const shown = groups.reduce((n, g) => n + g.visible.length, 0);
    return rows.length - shown;
  }, [groups, rows.length, search]);

  const hiddenGroupCount = useMemo(() => {
    if (!search.trim()) return 0;
    const all = new Set(rows.map((r) => r.group || GROUP_FALLBACK));
    return all.size - groups.length;
  }, [groups.length, rows, search]);

  // A change the filter is hiding is still going to be saved; say so.
  const changesOutsideFilter = useMemo(() => {
    if (!search.trim()) return 0;
    const visible = new Set(groups.flatMap((g) => g.visible.map((r) => r.permission)));
    return [...added, ...removed].filter((name) => !visible.has(name)).length;
  }, [groups, added, removed, search]);

  /* ---- Editing --------------------------------------------------------- */
  const setOne = useCallback((name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setGroup = useCallback((group, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      group.togglable.forEach((r) => { next[r.permission] = value; });
      return next;
    });
  }, []);

  const revert = useCallback(() => {
    setDraft(Object.fromEntries(rows.map((r) => [r.permission, r.value])));
  }, [rows]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const permissions = rows.filter((r) => draft[r.permission]).map((r) => r.permission);
      const response = await axiosClient.put(
        `/roles/${encodeURIComponent(role)}/permissions`,
        { permissions },
      );
      // The saved set becomes the new baseline, which clears the diff.
      setRows((prev) => prev.map((r) => ({ ...r, value: !!draft[r.permission] })));
      toast.success(response.data.message);
      onSaved?.(permissions.length);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [rows, draft, role, axiosClient, onSaved]);

  /* ---- `/` focuses search ---------------------------------------------- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  }

  const term = search.trim();

  return (
    <>
      <div style={{
        padding: '12px 18px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}>
        <Input
          ref={searchRef}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, description or group"
          prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
          suffix={!term ? (
            <span style={{
              font: "600 10.5px 'JetBrains Mono', monospace",
              color: 'var(--dim)',
              padding: '2px 6px',
              borderRadius: 5,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}>/</span>
          ) : <span />}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Button onClick={() => setCollapsed(
          groups.every((g) => collapsed[g.name])
            ? {}
            : Object.fromEntries(groups.map((g) => [g.name, true])),
        )}>
          {groups.every((g) => collapsed[g.name]) ? 'Expand all' : 'Collapse all'}
        </Button>
      </div>

      <div style={{ padding: '10px 18px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {term && (
          <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
            {`${rows.length - hiddenCount} match${rows.length - hiddenCount === 1 ? '' : 'es'}`}
          </div>
        )}

        {groups.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`Nothing matches "${term}".`}
            style={{ padding: '28px 0' }}
          />
        )}

        {groups.map((group) => {
          // A search always shows its matches; collapse is remembered and
          // restored when the box clears.
          const isCollapsed = term ? false : !!collapsed[group.name];
          return (
            <div key={group.name} className="et-access__group">
              <div
                className="et-access__grouphead"
                role="button"
                tabIndex={0}
                onClick={() => setCollapsed((p) => ({ ...p, [group.name]: !p[group.name] }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCollapsed((p) => ({ ...p, [group.name]: !p[group.name] }));
                  }
                }}
              >
                <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex' }}>
                  <Checkbox
                    checked={group.allOn}
                    indeterminate={group.indeterminate}
                    disabled={readOnly || group.togglable.length === 0}
                    // From mixed, a click drives to all-on: the destructive
                    // direction takes a second one.
                    onChange={() => setGroup(group, group.indeterminate ? true : !group.allOn)}
                  />
                </span>
                <span style={{ color: 'var(--dim)', fontSize: 10 }}>{isCollapsed ? '▸' : '▾'}</span>
                <span style={{ font: '700 13px Outfit, sans-serif' }}>{group.name}</span>
                {term && group.togglable.length > 0 && (
                  <Chip kind="deprecated">
                    {`TOGGLES ${group.visible.length} MATCH${group.visible.length === 1 ? '' : 'ES'}`}
                  </Chip>
                )}
                {group.changed > 0 && (
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--primary)' }} />
                )}
                <span className="et-access__meta" style={{ marginLeft: 'auto' }}>
                  {`${group.on} of ${group.rows.length} on`}
                  {term ? ` · ${group.visible.length} of ${group.rows.length} shown` : ''}
                  {group.changed > 0 ? ` · ${group.changed} changed` : ''}
                </span>
              </div>

              {!isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {group.visible.map((row) => {
                    const on = !!draft[row.permission];
                    const isAdded = on && !row.value;
                    const isRemoved = !on && row.value;
                    // A deprecated alias is pointless where its replacement is
                    // already held, so it is shown but not offered.
                    const supersededHere = row.deprecated && row.replacedBy && !!draft[row.replacedBy];
                    const disabled = readOnly || row.locked || supersededHere;

                    return (
                      <div
                        key={row.permission}
                        className={`et-access__row${
                          isAdded ? ' et-access__row--dirty' : ''
                        }${isRemoved ? ' et-access__row--removed' : ''}`}
                      >
                        <span style={{ justifySelf: 'center' }}>
                          <Switch
                            size="small"
                            checked={on}
                            disabled={disabled}
                            onChange={(v) => setOne(row.permission, v)}
                          />
                        </span>

                        <span className="et-access__rowtext">
                          <span className={`et-access__name${on ? '' : ' et-access__name--off'}${
                            isRemoved ? ' et-access__name--struck' : ''
                          }`}>
                            {row.permission}
                          </span>
                          <span className={`et-access__desc${on ? '' : ' et-access__desc--off'}`}>
                            {row.locked ? row.lockReason : row.description}
                            {/* The description already names the replacement; this
                                adds only why the toggle is not offered here. */}
                            {supersededHere && ' Already covered on this role.'}
                            {row.matchedOn && row.matchedOn !== 'name' && (
                              <span style={{ color: 'var(--dim)', fontStyle: 'italic' }}>
                                {` — matched on ${row.matchedOn}`}
                              </span>
                            )}
                          </span>
                        </span>

                        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
                          {isAdded && <Chip kind="added">ADDED</Chip>}
                          {isRemoved && <Chip kind="removed">REMOVED</Chip>}
                          {row.locked && <Chip kind="locked">LOCKED</Chip>}
                          {row.deprecated && <Chip kind="deprecated">DEPRECATED</Chip>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {term && hiddenCount > 0 && (
          <div style={{
            font: '400 12px Outfit, sans-serif',
            color: 'var(--muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            paddingTop: 4,
          }}>
            {`${hiddenCount} permission${hiddenCount === 1 ? '' : 's'}`}
            {hiddenGroupCount > 0 && ` in ${hiddenGroupCount} group${hiddenGroupCount === 1 ? '' : 's'}`}
            {' hidden by this filter.'}
            <Button type="link" size="small" onClick={() => setSearch('')} style={{ padding: 0 }}>
              Clear filter
            </Button>
          </div>
        )}
      </div>

      {dirty && !readOnly && (
        <div className="et-access__savebar">
          <div>
            <div style={{ font: '600 12.5px Outfit, sans-serif' }}>Unsaved changes</div>
            <div className="et-access__meta">
              {[added.length ? `${added.length} added` : null, removed.length ? `${removed.length} removed` : null]
                .filter(Boolean).join(' · ')}
              {changesOutsideFilter > 0
                && ` · ${changesOutsideFilter} outside the current filter`}
            </div>
          </div>
          <div className="et-access__savebar-actions">
            <Button onClick={revert} disabled={saving}>Revert</Button>
            <Button type="primary" onClick={save} loading={saving}>Save changes</Button>
          </div>
        </div>
      )}
    </>
  );
}

export default RolePermissions;
